import { FC, useRef, useState, KeyboardEvent } from 'react';
import classNames from 'classnames';

import Button, { ButtonSize, ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import Radio from '@zvuk-b2b/react-uikit/ui/Radio';
import IconChevronDown from '@zvuk-b2b/react-uikit/ui/icons/ChevronDown';
import IconChevronUp from '@zvuk-b2b/react-uikit/ui/icons/ChevronUp';
import Dropdown, { DropdownItemDataType } from '@zvuk-b2b/react-uikit/ui/Dropdown';
import ToolkitUtils from '@zvuk-b2b/react-uikit/toolkit/utils';

import { usePlayerContext } from 'application/providers/PlayerProvider';
import { PlayButton } from 'application/components/PlayButton';
import MediaListItemProgressSlider from 'application/components/MediaList/MediaListItem/MediaListItemProgressSlider';
import FileUtils from 'application/utils/FileUtils';
import { PromoRollSynthBackgroundType } from '@entities/promo-roll-synth-job/model';

import { TEXTS } from '../PromoRollSynthModal/texts';
import styles from './BackgroundSelect.module.scss';

type Props = {
  value: string;
  options: PromoRollSynthBackgroundType[];
  onChange: (id: string) => void;
};

const SAMPLE_ID_PREFIX = 'bg_sample';

const BackgroundSelect: FC<Props> = ({ value, options, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { play, pause, resume, isPaused, isLoading, fileUuid, progress, changeProgress } = usePlayerContext();

  const selectedOption = options.find((b) => b.backgroundId === value);

  const handleSampleClick = (bg: PromoRollSynthBackgroundType) => {
    const sampleId = `${SAMPLE_ID_PREFIX}_${bg.id}`;

    if (fileUuid === sampleId && !isPaused) {
      pause();
    } else if (fileUuid === sampleId && isPaused) {
      resume();
    } else {
      play({ fileUuid: sampleId, fileName: bg.title, fileUrl: FileUtils.generateMediafileURL(bg.fileUrl) });
    }
  };

  const handleSelect = (bg: PromoRollSynthBackgroundType) => {
    onChange(bg.backgroundId);
    setIsOpen(false);
  };

  const handleSampleKeyDown = (e: KeyboardEvent, bg: PromoRollSynthBackgroundType) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.stopPropagation();
      handleSampleClick(bg);
    }
  };

  const items: DropdownItemDataType[] = options.map((bg) => {
    const sampleId = `${SAMPLE_ID_PREFIX}_${bg.id}`;
    const isSampleActive = fileUuid === sampleId;
    const isPlaying = isSampleActive && !isPaused;

    return {
      key: bg.id,
      value: bg.title,
      selected: value === bg.backgroundId,
      onClick: () => handleSelect(bg),
      content: (
        <>
          <Radio
            elementId={`bg_radio_${bg.id}`}
            isChecked={value === bg.backgroundId}
            onChange={() => handleSelect(bg)}
          />

          <div
            role="button"
            tabIndex={0}
            data-testid={`bg_sample_${bg.id}`}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              handleSampleClick(bg);
            }}
            onKeyDown={(e) => handleSampleKeyDown(e, bg)}>
            <PlayButton
              isMediafilePlaying={isPlaying}
              isMediaFilePaused={isSampleActive && isPaused}
              isLoading={isSampleActive && isLoading}
            />
          </div>

          <span className={styles.optionLabel}>{bg.title}</span>

          {isSampleActive && (
            <MediaListItemProgressSlider
              elementId={`bg_progress_${bg.id}`}
              progress={progress}
              onChange={changeProgress}
            />
          )}
        </>
      ),
    };
  });

  return (
    <div
      className={styles.container}
      ref={containerRef}>
      <span className={styles.label}>{TEXTS.FIELD_BACKGROUND_LABEL}</span>

      <Button
        ref={triggerRef}
        elementId="background_select_trigger"
        type={ButtonType.SECONDARY}
        size={ButtonSize.MEDIUM}
        className={classNames(styles.trigger, { [styles.triggerOpen]: isOpen })}
        onClick={() => setIsOpen((v) => !v)}>
        <span className={classNames({ [styles.placeholder]: !selectedOption })}>
          {selectedOption?.title ?? TEXTS.FIELD_BACKGROUND_PLACEHOLDER}
        </span>
        {isOpen ? <IconChevronUp /> : <IconChevronDown />}
      </Button>

      {isOpen && (
        <Dropdown
          elementId="background_select_dropdown"
          className={styles.dropdown}
          itemContentClassName={styles.optionContent}
          items={items}
          anchorNode={triggerRef.current!}
          parentNode={containerRef.current!}
          style={{ width: ToolkitUtils.getWidth(triggerRef.current!) }}
          onClose={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default BackgroundSelect;
